import { Copy01, Edit01, Link01, Trash01 } from '@untitledui/icons';
import type { FC } from 'react';
import {
  Fragment,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { toast } from 'sonner';

import { useBreakpoint } from '@/hooks/useBreakpoint';
import { Dropdown } from '@/lib/base/overlays/dropdown';
import { cx } from '@/utils/cx';

import { useLongPress } from '../hooks/useLongPress';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface RowContextMenuAction {
  /** Unique identifier for the action */
  key: string;
  /** Display label */
  label: string;
  /** Optional icon component */
  icon?: FC<{ className?: string }>;
  /** Whether this action is destructive (rendered in error colour) */
  isDestructive?: boolean;
  /** Whether this action is disabled */
  isDisabled?: boolean;
  /** Called when the action is selected */
  onAction: () => void | Promise<void>;
}

export interface RowContextMenuProps {
  /** Entity ID — used in aria-label */
  entityId: string;
  /** Human-readable entity name for aria-label */
  entityName?: string;
  /** Called when Edit is selected */
  onEdit?: () => void;
  /** Called when Delete is selected */
  onDelete?: () => void;
  /** Called when Duplicate is selected */
  onDuplicate?: () => void;
  /** Called when Copy link is selected */
  onCopyLink?: () => void;
  /** Extra entity-type-specific actions rendered after the standard set */
  extraActions?: RowContextMenuAction[];
  /** Trigger element children (defaults to dots button) */
  children?: React.ReactNode;
}

// ---------------------------------------------------------------------------
// Bottom sheet (mobile)
// ---------------------------------------------------------------------------

interface BottomSheetProps {
  isOpen: boolean;
  onClose: () => void;
  actions: RowContextMenuAction[];
}

const FOCUSABLE_SELECTORS =
  'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

function BottomSheet({ isOpen, onClose, actions }: BottomSheetProps) {
  const sheetRef = useRef<HTMLDivElement>(null);

  // Move focus into the sheet when it opens
  useEffect(() => {
    if (!isOpen) {
      return;
    }
    const sheet = sheetRef.current;
    if (!sheet) {
      return;
    }
    const firstFocusable =
      sheet.querySelector<HTMLElement>(FOCUSABLE_SELECTORS);
    if (firstFocusable) {
      firstFocusable.focus();
    } else {
      sheet.focus();
    }
  }, [isOpen]);

  const handleBottomSheetAction = useCallback(
    async (actionKey: React.Key) => {
      const action = actions.find(a => a.key === actionKey);
      if (action) {
        try {
          await action.onAction();
        } catch (err) {
          toast.error('Action failed. Please try again.');
          console.error('Context menu action failed:', err);
        }
      }
      onClose();
    },
    [actions, onClose]
  );

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLDivElement>) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        onClose();
        return;
      }

      // Trap focus within the sheet on Tab / Shift+Tab
      if (e.key === 'Tab') {
        const sheet = sheetRef.current;
        if (!sheet) {
          return;
        }
        const focusable = Array.from(
          sheet.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTORS)
        );
        if (focusable.length === 0) {
          e.preventDefault();
          return;
        }
        const first = focusable[0];
        const last = focusable[focusable.length - 1];
        if (e.shiftKey) {
          if (document.activeElement === first) {
            e.preventDefault();
            last.focus();
          }
        } else {
          if (document.activeElement === last) {
            e.preventDefault();
            first.focus();
          }
        }
      }
    },
    [onClose]
  );

  if (!isOpen) {
    return null;
  }

  return (
    <>
      {/* Backdrop */}
      <div
        className='fixed inset-0 z-40 bg-black/40'
        onClick={onClose}
        aria-hidden='true'
      />
      {/* Sheet */}
      <div
        ref={sheetRef}
        role='dialog'
        aria-modal='true'
        aria-label='Row actions'
        // tabIndex allows the container itself to receive focus as a fallback
        tabIndex={-1}
        onKeyDown={handleKeyDown}
        className='fixed bottom-0 left-0 right-0 z-50 rounded-t-2xl bg-[var(--color-bg-primary)] shadow-xl pb-safe'
      >
        <div className='mx-auto mt-2 mb-4 h-1 w-10 rounded-full bg-[var(--color-border-primary)]' />
        <ul role='menu' className='py-2 max-h-[60vh] overflow-y-auto'>
          {actions.map(action => (
            <li key={action.key} role='none'>
              <button
                type='button'
                role='menuitem'
                disabled={action.isDisabled}
                onClick={() => handleBottomSheetAction(action.key)}
                className={cx(
                  'flex w-full items-center gap-3 px-5 py-3.5 text-sm font-medium',
                  'disabled:opacity-40 active:bg-[var(--color-bg-tertiary)]',
                  action.isDestructive
                    ? 'text-[var(--color-text-error-primary)]'
                    : 'text-[var(--color-text-primary)]'
                )}
              >
                {action.icon && (
                  <action.icon className='size-5 shrink-0' aria-hidden='true' />
                )}
                {action.label}
              </button>
            </li>
          ))}
        </ul>
      </div>
    </>
  );
}

// ---------------------------------------------------------------------------
// RowContextMenu
// ---------------------------------------------------------------------------

/**
 * RowContextMenu provides per-row context actions accessible via:
 * - Right-click anywhere on the row
 * - Clicking the visible dots button
 * - Keyboard: Shift+F10 or the Menu key when the row is focused
 * - Touch: long-press on the row
 *
 * On mobile (<md breakpoint) renders as a bottom sheet instead of a popover.
 */
export function RowContextMenu({
  entityId,
  entityName,
  onEdit,
  onDelete,
  onDuplicate,
  onCopyLink,
  extraActions = [],
  children,
}: RowContextMenuProps) {
  const isMobile = !useBreakpoint('md');
  const [bottomSheetOpen, setBottomSheetOpen] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const triggerRef = useRef<HTMLButtonElement>(null);

  const label = entityName ? `Actions for ${entityName}` : 'Row actions';

  // Build the canonical action list used by both the desktop popover and the
  // mobile bottom sheet.
  const actions = useMemo<RowContextMenuAction[]>(
    () => [
      ...(onEdit
        ? [
            {
              key: 'edit',
              label: 'Edit',
              icon: Edit01,
              onAction: onEdit,
            },
          ]
        : []),
      ...(onDuplicate
        ? [
            {
              key: 'duplicate',
              label: 'Duplicate',
              icon: Copy01,
              onAction: onDuplicate,
            },
          ]
        : []),
      ...(onCopyLink
        ? [
            {
              key: 'copy-link',
              label: 'Copy link',
              icon: Link01,
              onAction: onCopyLink,
            },
          ]
        : []),
      ...extraActions,
      ...(onDelete
        ? [
            {
              key: 'delete',
              label: 'Delete',
              icon: Trash01,
              isDestructive: true,
              onAction: onDelete,
            },
          ]
        : []),
    ],
    [onEdit, onDuplicate, onCopyLink, extraActions, onDelete]
  );

  const handleDropdownAction = useCallback(
    async (key: React.Key) => {
      const action = actions.find(a => a.key === key);
      if (action) {
        try {
          await action.onAction();
        } catch (err) {
          toast.error('Action failed. Please try again.');
          console.error('Context menu action failed:', err);
        }
      }
      setIsMenuOpen(false);
      triggerRef.current?.focus();
    },
    [actions, setIsMenuOpen]
  );

  const openMenu = useCallback(() => {
    if (isMobile) {
      setBottomSheetOpen(true);
    } else {
      setIsMenuOpen(true);
    }
  }, [isMobile]);

  const closeBottomSheet = useCallback(() => {
    setBottomSheetOpen(false);
    triggerRef.current?.focus();
  }, []);

  // Keyboard trigger: Shift+F10 or Menu key opens the context menu
  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'ContextMenu' || (e.key === 'F10' && e.shiftKey)) {
        e.preventDefault();
        openMenu();
      }
    },
    [openMenu]
  );

  // Long-press for touch
  const longPressHandlers = useLongPress({
    threshold: 300,
    onLongPress: _touchData => {
      // _touchData contains stable position info if needed for positioning
      openMenu();
    },
  });

  // Right-click handler (attach to the row wrapper via data attribute and
  // handled externally, or the trigger button itself)
  const handleContextMenu = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      openMenu();
    },
    [openMenu]
  );

  const defaultTrigger = (
    <span aria-hidden='true' className='flex gap-px'>
      {[0, 1, 2].map(i => (
        <span key={i} className='size-1 rounded-full bg-current' />
      ))}
    </span>
  );

  if (isMobile) {
    return (
      <>
        <button
          ref={triggerRef}
          type='button'
          aria-label={label}
          aria-haspopup='dialog'
          aria-expanded={bottomSheetOpen}
          onClick={openMenu}
          onKeyDown={handleKeyDown}
          {...longPressHandlers}
          data-entity-id={entityId}
          className='flex items-center justify-center rounded-md p-2.5 text-[var(--color-text-tertiary)]
            hover:bg-[var(--color-bg-tertiary)] hover:text-[var(--color-text-secondary)]
            focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--color-brand-600)]
            transition-colors duration-75'
        >
          {children ?? defaultTrigger}
        </button>
        <BottomSheet
          isOpen={bottomSheetOpen}
          onClose={closeBottomSheet}
          actions={actions}
        />
      </>
    );
  }

  return (
    <Dropdown.Root isOpen={isMenuOpen} onOpenChange={setIsMenuOpen}>
      <button
        ref={triggerRef}
        type='button'
        slot='trigger'
        aria-label={label}
        aria-haspopup='menu'
        aria-expanded={isMenuOpen}
        onKeyDown={handleKeyDown}
        onContextMenu={handleContextMenu}
        {...longPressHandlers}
        data-entity-id={entityId}
        className='flex items-center justify-center rounded-md p-1.5 text-[var(--color-text-tertiary)]
          hover:bg-[var(--color-bg-tertiary)] hover:text-[var(--color-text-secondary)]
          focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--color-brand-600)]
          transition-colors duration-75'
      >
        {children ?? defaultTrigger}
      </button>
      <Dropdown.Popover>
        <Dropdown.Menu
          onAction={handleDropdownAction}
          disabledKeys={actions.filter(a => a.isDisabled).map(a => a.key)}
        >
          {actions.map((action, index) => {
            const isFirst = index === 0;
            const isFirstDestructive =
              action.isDestructive &&
              !actions.slice(0, index).some(a => a.isDestructive);
            const showSeparator = !isFirst && isFirstDestructive;

            return (
              <Fragment key={action.key}>
                {showSeparator && <Dropdown.Separator />}
                <Dropdown.Item
                  id={action.key}
                  label={action.label}
                  icon={action.icon}
                  isDisabled={action.isDisabled}
                  className={
                    action.isDestructive
                      ? state =>
                          cx(
                            'text-[var(--color-text-error-primary)]',
                            typeof state === 'object' &&
                              'isFocused' in state &&
                              state.isFocused &&
                              'bg-[var(--color-bg-tertiary)]'
                          )
                      : undefined
                  }
                />
              </Fragment>
            );
          })}
        </Dropdown.Menu>
      </Dropdown.Popover>
    </Dropdown.Root>
  );
}

/**
 * Props for the row-level context menu event handlers.
 * Attach these to a row wrapper to enable right-click and long-press opening.
 */
export interface RowContextTriggerProps {
  onContextMenu: (e: React.MouseEvent) => void;
  onTouchStart: (e: React.TouchEvent) => void;
  onTouchEnd: () => void;
  onTouchMove: () => void;
}
